import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex justify-content-between align-items-center" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = {
  key: 0,
  class: "preview-file__wrapper"
}
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = ["src"]
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 4,
  class: "preview-file__icon"
}
const _hoisted_8 = { class: "file-name" }
const _hoisted_9 = { class: "file-meta" }
const _hoisted_10 = {
  key: 1,
  class: "p-3"
}

import { computed, ref, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import { EMIT } from '@/enum';
import { useMessageCenterStore } from '@/store';
import PDFViewer from '@/components/PDFViewer/PDFViewer.vue';
import download from 'downloadjs';
import { FileInterface } from '@/types';

interface Normalized {
  name: string;
  type: string;
  blob: Blob;
}

interface PropsInterface {
  title: string;
  visible?: boolean;
  file?: Blob | File | FileInterface;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PreviewFileModal',
  props: {
    title: {},
    visible: { type: Boolean, default: false },
    file: { default: undefined }
  },
  emits: ["download", "update:visible"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const messageCenterStore = useMessageCenterStore();
const { attachmentFile } = storeToRefs(messageCenterStore);

const fallbackTitle = ref('File Preview');

const normalizedFile = computed<Normalized>(() => {
  if (props.file instanceof Blob) {
    const f = props.file as File | Blob;
    const name = (f as File)?.name;
    const type = f.type || 'application/octet-stream';
    return { name, type, blob: f };
  }

  if (props.file && !(props.file instanceof Blob)) {
    const fi = props.file as FileInterface;
    const type = fi.type || 'application/octet-stream';
    const blob = new Blob([fi.content as ArrayBuffer | Uint8Array], { type });
    return { name: fi.name, type, blob };
  }

  if (attachmentFile.value) {
    const type = attachmentFile.value.type || 'application/octet-stream';
    const blob = new Blob([attachmentFile.value.content], { type });
    return { name: attachmentFile.value.name, type, blob };
  }

  return null;
});

let objectUrl: string | null = null;

onUnmounted(() => {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  attachmentFile.value = undefined;
});

const blobUrl = computed(() => {
  if (!normalizedFile.value) return '';
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = URL.createObjectURL(normalizedFile.value.blob);
  return objectUrl;
});

const fileExtension = computed(() => {
  const name = normalizedFile.value?.name?.toLowerCase() || '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.substring(dot) : '';
});

const isImage = computed(() => normalizedFile.value?.type.startsWith('image/'));
const isAudio = computed(() => normalizedFile.value?.type.startsWith('audio/'));
const isVideo = computed(() => normalizedFile.value?.type.startsWith('video/'));
const isPDF = computed(() => normalizedFile.value?.type === 'application/pdf');
const isOther = computed(() => !isImage.value && !isAudio.value && !isVideo.value && !isPDF.value);

const onCancel = () => {
  emit('update:visible', false);
  emit(EMIT.CANCEL);
};

const onDownload = () => {
  if (!normalizedFile.value) return;
  const { blob, name, type } = normalizedFile.value;
  download(blob, name, type || 'application/octet-stream');
};

const onUpdateVisible = (value: boolean) => {
  if (!value) onCancel();
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_unref(Dialog), {
    visible: __props.visible,
    header: normalizedFile.value?.name || __props.title || fallbackTitle.value,
    modal: true,
    draggable: false,
    class: _normalizeClass(isOther.value || isAudio.value ? 'w-6 preview-file__modal' : 'w-11 preview-file__modal'),
    "content-class": "p-0 bg-white overflow-x-hidden",
    onHide: onCancel,
    "onUpdate:visible": onUpdateVisible
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h4", _hoisted_2, _toDisplayString(normalizedFile.value?.name || __props.title || fallbackTitle.value), 1),
        (normalizedFile.value && !isOther.value && !__props.file)
          ? _withDirectives((_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              type: "secondary",
              "icon-right": "system_update_alt",
              onClick: onDownload
            }, null, 512)), [
              [_directive_tooltip, 'Download file']
            ])
          : _createCommentVNode("", true)
      ])
    ]),
    default: _withCtx(() => [
      (normalizedFile.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (isImage.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: blobUrl.value,
                  alt: normalizedFile.value.name,
                  class: "preview-image"
                }, null, 8, _hoisted_4))
              : (isAudio.value)
                ? (_openBlock(), _createElementBlock("audio", {
                    key: 1,
                    controls: "",
                    src: blobUrl.value,
                    class: "preview-audio"
                  }, null, 8, _hoisted_5))
                : (isVideo.value)
                  ? (_openBlock(), _createElementBlock("video", {
                      key: 2,
                      controls: "",
                      src: blobUrl.value,
                      class: "preview-video"
                    }, null, 8, _hoisted_6))
                  : (isPDF.value)
                    ? (_openBlock(), _createBlock(PDFViewer, {
                        key: 3,
                        name: normalizedFile.value.name,
                        src: blobUrl.value,
                        class: "preview-pdf",
                        onDownload: onDownload
                      }, null, 8, ["name", "src"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        _cache[0] || (_cache[0] = _createElementVNode("i", { class: "pi pi-file" }, null, -1)),
                        _createElementVNode("p", _hoisted_8, _toDisplayString(normalizedFile.value.name), 1),
                        _createElementVNode("p", _hoisted_9, "Type: " + _toDisplayString(fileExtension.value), 1),
                        (!props.file)
                          ? (_openBlock(), _createBlock(_component_AppButton, {
                              key: 0,
                              text: "Download file",
                              type: "secondary",
                              "icon-right": "system_update_alt",
                              onClick: onDownload
                            }))
                          : _createCommentVNode("", true)
                      ]))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, "There is no file to preview."))
    ]),
    _: 1
  }, 8, ["visible", "header", "class"]))
}
}

})